import React from 'react';
import { useNavigate as useReactRouterNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import i18n, {
  SUPPORTED_LANGUAGES,
  LANG_STORAGE_KEY,
  type SupportedLanguage,
} from '../i18n';
import { localizedPath, readLangFromPath } from '../i18n/router';

// Ordre d'affichage identique au site marketing (cf. js/i18n-switcher.js DISPLAY_ORDER)
const DISPLAY_ORDER: SupportedLanguage[] = ['nl', 'en', 'fr', 'de'];

const LANG_META: Record<SupportedLanguage, { label: string; flag: string }> = {
  fr: { label: 'FR', flag: 'fr' },
  en: { label: 'EN', flag: 'gb' },
  nl: { label: 'NL', flag: 'nl' },
  de: { label: 'DE', flag: 'de' },
};

const LANGBAR_HEIGHT = 48;

const BTLangBar: React.FC = () => {
  const { t } = useTranslation('i18n');
  const navigate = useReactRouterNavigate();
  const location = useLocation();

  const current =
    readLangFromPath(location.pathname) ||
    (i18n.language as SupportedLanguage) ||
    'fr';

  const handleSwitch = (lang: SupportedLanguage) => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* localStorage indispo : on continue */
    }
    void i18n.changeLanguage(lang);
    const target = localizedPath(location.pathname, lang) + location.search + location.hash;
    navigate(target, { replace: false });
  };

  return (
    <Box
      component="nav"
      role="navigation"
      aria-label="Language switcher"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        minHeight: `${LANGBAR_HEIGHT}px`,
        background: '#D89D2F',
        borderBottom: '1px solid #B5811F',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        fontSize: 13,
        py: '6px',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 1.5, sm: 2.25 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          gap: { xs: 1, sm: 2.25 },
          flexWrap: 'wrap',
        }}
      >
        <Box
          component="span"
          sx={{
            color: '#fff',
            opacity: 0.85,
            mr: 0.75,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 11,
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          {t('languageLabel')}
        </Box>

        <Box
          component="ul"
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            listStyle: 'none',
            m: 0,
            p: 0,
          }}
        >
          {DISPLAY_ORDER.map((lang) => {
            const meta = LANG_META[lang];
            const isActive = lang === current;
            return (
              <Box component="li" key={lang} sx={{ m: 0, p: 0 }}>
                <Box
                  component="a"
                  href={localizedPath(location.pathname, lang) + location.search + location.hash}
                  hrefLang={lang}
                  lang={lang}
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={t(`languages.${lang}`)}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    handleSwitch(lang);
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    py: '5px',
                    px: { xs: '6px', sm: '10px' },
                    borderRadius: '6px',
                    color: '#fff',
                    textDecoration: 'none',
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                    transition: 'background 0.18s ease, transform 0.18s ease',
                    background: isActive ? 'rgba(255,255,255,0.26)' : 'transparent',
                    boxShadow: isActive ? 'inset 0 -2px 0 #fff' : 'none',
                    '&:hover, &:focus': {
                      background: isActive
                        ? 'rgba(255,255,255,0.26)'
                        : 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      textDecoration: 'none',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={`https://flagcdn.com/24x18/${meta.flag}.png`}
                    alt={t(`languages.${lang}`)}
                    loading="lazy"
                    width={22}
                    height={16}
                    sx={{
                      borderRadius: '2px',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
                      flexShrink: 0,
                      objectFit: 'cover',
                    }}
                  />
                  <Box component="span" sx={{ fontSize: { xs: 12, sm: 13 }, letterSpacing: '0.05em' }}>
                    {meta.label}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export { LANGBAR_HEIGHT };
export default BTLangBar;
